import { ITag } from "@app/interfaces/ITag";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { VeriSearchBoxComponent } from "@directives/_veri/veriSearchBox/veriSearchBox.directive";
import { ResourceType } from "@enums/ResourceType";
import { SortDirection } from "@enums/SortDirection";
import { SortType } from "@enums/SortType";
import { IComboBox } from "@interfaces/IComboBox";
import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { ITagFilter } from "@interfaces/filters/ITagFilter";
import { ChartType } from "@services/charts.service";
import { ConstantsService } from "@services/constantsService";
import { GroupService } from "@services/group.service.ajs";
import { HTMLService } from "@services/htmlService.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import { IVeriPillNavLabel } from "../veriSimpleNav/veriSimpleNav.component";
import "./veriTable.scss";
import { IVeriTableContainer } from "./veriTableContainer/veriTableContainer.component";

export enum VeriTableRowStatus {
    OK,
    OkDash,
    OkChecked,
    Warning,
    Error
}

export interface IVeriTableHeader {
    id?: string;
    title?: string;
    width?: number;
    className?: string;
    sortable?: boolean;
    excludeFromSearch?: boolean;
    sortType?: SortType;
    alignRight?: boolean;
    sortDirection?: SortDirection;
    templateUrl?: string;
    template?: string;
    hide?: boolean;
    excludeFromExport?: boolean;

    hideOnMobile?: boolean;
    hideIfNoData?: boolean;
    includeInSummary?: boolean;

    graphOptions?: IVeriTableCellGraphOptions;
}

export interface IVeriTableCellGraphOptions {
    show: boolean;
    chartType: ChartType;

    groupBy?: string | IVeriTableHeader | number;
    maxSeries?: number;

    title?: string;

    toggleShowGraph?: () => void;

    stacked?: boolean;
    dateColumn?: number;
}

export interface IVeriTableRow<T = any> {
    cells?: IVeriTableRowCell[];
    data?: T;

    searchString?: string;
    rowClassName?: string;

    customRowStyle?: string;

    dropDownFilterValues?: number[];
    navPillFilterValues?: number[];

    rowWarningStatus?: VeriTableRowStatus;

    title?: string;

    isInGroup?: (data: T, group: IGroupWithIds) => boolean;

    children?: IVeriTableRow<T>[];
    eventSummaryColour?: string;
}

export interface IVeriTableRowCell {
    contents?: string;
    sortData?: any;
    data?: any;
    className?: string;
    hasNoData?: boolean;
    sanitize?: boolean;

    createSearchCallback?: (data: any) => string;

    exportData?: string;
    exportDataMethod?: () => angular.IPromise<string>;
    excludeFromExport?: boolean;

    header?: IVeriTableHeader;
    summaryIcon?: string;
    summaryLabel?: string;
    summarySubLabel?: string;
    summaryColour?: string;
    excludeFromSummary?: boolean;

    graphLabel?: string;
    showTooltip?: boolean;
}

export interface IVeriTableBindings<T = any> {
    headers: IVeriTableHeader[];
    rows: IVeriTableRow<T>[];
    displayRows: IVeriTableRow<T>[];

    setFilterRows(rows: IVeriTableRow<T>[]);

    loading: boolean;

    highlightSelectedItems: boolean;
    selectedItem: any;
    selectedItemIdentifier: string;
    sortColumn: number;
    sortDirection: SortDirection;

    showStatusColumn: boolean;
    showSearch: boolean;
    showRows: boolean;
    graphOptionsAvailable?: boolean;
    clickable: boolean;
    downloadable: boolean;
    withoutGroups: boolean;

    maxRows: number;
    searchPlaceholder: string;
    searchString: string;
    searchBox: VeriSearchBoxComponent;
    searchRows: (forceDigest: boolean) => void;

    showTagFilter: boolean;

    showGroupFilter: boolean;
    dropDownFilterOptions: IComboBox[];
    selectedDropDownFilterOption: number;

    navPillFilterOptions: IVeriPillNavLabel[];
    selectedNavPillFilterOption: number;

    exportAsLandscape: boolean;
    exportTitle: string;
    hideExport: boolean;

    veriTableContainer: IVeriTableContainer;

    lastAvailableDataDate: string;

    allUserGroupIds: number[];
    allGeofenceGroupIds: number[];
    allVehicleGroupIds: number[];
    allDriverGroupIds: number[];

    onGetLastAvailableData: () => void;
    onRowsUpdated: (args: { rows: IVeriTableRow<T>[] }) => void;
    onRowClicked: (args: { row: IVeriTableRow<T>, $event: MouseEvent }) => void;
    onRowRightClicked: (args: { row: IVeriTableRow<T>, $event: MouseEvent }) => void;
    onRowHighlighted: (args: { row: IVeriTableRow<T>, $event: MouseEvent }) => void;
}

class VeriTableComponentController implements angular.IComponentController, IVeriTableBindings<any> {

    parentVeriTable: IVeriTableBindings<any>;
    veriTableContainer: IVeriTableContainer;

    headers: IVeriTableHeader[];
    rows: IVeriTableRow<any>[];

    loading: boolean;

    displayRows: IVeriTableRow<any>[];
    filterRows: IVeriTableRow<any>[];

    highlightSelectedItems: boolean;
    selectedItem: any;
    selectedItemIdentifier: string;
    selectedItemIndex: number;
    ignoreNextSelectedItemChange: boolean;

    highlightedRow: IVeriTableRow<any>;

    maxRows: number;
    sortColumn: number;
    sortDirection: SortDirection;

    showStatusColumn: boolean;
    showRows: boolean;
    showSearch: boolean;
    clickable: boolean;
    downloadable: boolean = true;
    withoutGroups: boolean = false;

    searchString: string;
    searchBox: VeriSearchBoxComponent;
    searchPlaceholder: string;

    showTagFilter: boolean;
    showGraphOptions: boolean;
    showGroupFilter: boolean;
    selectedGroupRowCount: number;

    dropDownFilterOptions: IComboBox[];
    selectedDropDownFilterOption: number;

    navPillFilterOptions: IVeriPillNavLabel[];
    selectedNavPillFilterOption: number;

    showResourceFilter: boolean;
    hasEmptyState: boolean;

    tagResourceType: string;

    resourceCount: number;

    exportAsLandscape: boolean;
    exportTitle: string;
    hideExport: boolean;

    lastAvailableDataDate: string;

    showCompactFilters: boolean = false;

    allUserGroupIds: number[];
    allGeofenceGroupIds: number[];
    allVehicleGroupIds: number[];
    allDriverGroupIds: number[];


    onGetLastAvailableData: () => void;
    onRowsUpdated: (args: { rows: IVeriTableRow<any>[] }) => void;
    onRowClicked: (args: { row: IVeriTableRow<any>, $event: MouseEvent }) => void;
    onRowRightClicked: (args: { row: IVeriTableRow<any>, $event: MouseEvent }) => void;
    onRowHighlighted: (args: { row: IVeriTableRow<any>, $event: MouseEvent }) => void;

    // ----

    private rowType: ResourceType;
    private selectedGroup: IGroupWithIds;
    private selectedTag: ITag;
    private hasPreparedRowsForSearchAndSort = false;

    static $inject = ["$scope", "$element", "$transclude", "$timeout", "$templateCache", "$sanitize", "groupService", "veriDate", "constantsService", "translationService", "htmlService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $element: JQuery,
        private readonly $transclude: angular.ITranscludeFunction,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $templateCache: angular.ITemplateCacheService,
        private readonly $sanitize, // sanitizer does not come with a type
        private readonly groupService: GroupService,
        private readonly veriDate: VeriDate,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService,
        private readonly htmlService: HTMLService
    ) {
    }

    $onInit(): void {
        this.setDefaults();
        this.showResourceFilter = this.$transclude.isSlotFilled("filters");
        this.hasEmptyState = this.$transclude.isSlotFilled("emptyState");

        this.updateRowType();

        // Ensures nested tables dont register with a parent veri table container
        if (this.veriTableContainer && !this.parentVeriTable) {
            this.veriTableContainer.registerChild(this);
        }

        if (!this.withoutGroups) {
            this.getGroups();
        }


        this.$timeout(() => {
            this.checkForCompactMode();
        }, 0);
    }

    getGroups() {
        this.groupService.getGroupsWithFullMappings().then(groups => {
            this.allUserGroupIds = groups?.flatMap(groups => groups.userIds);
            this.allGeofenceGroupIds = groups?.flatMap(groups => groups.GeofenceIds);
            this.allVehicleGroupIds = groups?.flatMap(groups => groups.VehicleIds);
            this.allDriverGroupIds = groups?.flatMap(groups => groups.DriverIds);
        })
    }

    sanitize(value: string): string {
        return this.$sanitize(value);
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        this.setDefaults();

        var sortRows = false;
        var searchRows = false;

        if (onChangesObj["dropDownFilterOptions"] && this.dropDownFilterOptions && this.dropDownFilterOptions[0] && !this.selectedDropDownFilterOption) {
            this.selectedDropDownFilterOption = this.dropDownFilterOptions[0].Value;
        }

        if (onChangesObj["navPillFilterOptions"] && this.navPillFilterOptions && this.navPillFilterOptions[0] && !this.selectedNavPillFilterOption) {
            this.selectedNavPillFilterOption = this.navPillFilterOptions.findIndex(p => p.highlighted);
        }

        if (onChangesObj["headers"] && this.headers) {
            this.prepareHeaders();

            sortRows = true;
        }

        if (onChangesObj["rows"] && this.rows) {

            this.prepareRows();
            this.hasPreparedRowsForSearchAndSort = false;

            sortRows = true;
            searchRows = !onChangesObj["rows"].isFirstChange();
        }

        if (onChangesObj["searchString"]) {
            searchRows = true;
        }

        if (onChangesObj["selectedItem"] && onChangesObj["selectedItem"].currentValue != onChangesObj["selectedItem"].previousValue) {
            if (!this.ignoreNextSelectedItemChange) {
                this.calculateSelectedItemIndex();
            }
        }

        if (searchRows && this.searchString || this.selectedGroup) {
            this.searchRows();
        }
        else if (sortRows) {
            this.sortRows();
        }

        if (this.maxRows !== undefined && this.maxRows > 0) {
            this.displayRows = this.displayRows.slice(0, this.maxRows);
        }

        // Ensure we only send updates for row changes
        if (onChangesObj["rows"] && this.veriTableContainer) {
            this.veriTableContainer.onRowsUpdated();
        }

        this.showHideColumnsWithNoData();

        this.resourceCount = this.displayRows?.length;
    }

    showHideColumnsWithNoData() {

        if (!this.headers || !this.rows) {
            return;
        }

        var expectedArrayLength = this.headers.length;
        if (this.rows.length && this.rows[0].cells) {
            expectedArrayLength = Math.max(this.rows[0].cells.length, this.headers.length);
        }

        var columnHasNoDataArray = this.rows.map(r => r.cells.map(r => (r.hasNoData ? 1 : 0) as number));
        var columnHasNoDataMap = columnHasNoDataArray.reduce((sum, current) => {
            var summedArray: number[] = [];

            sum.forEach((s, index) => {
                summedArray.push(s + current[index]);
            });


            return summedArray;
        }, new Array(expectedArrayLength).fill(0));

        this.headers.forEach((h, i) => {
            if (h.hideIfNoData) {

                // Else show / hide it if we have no data
                if (columnHasNoDataMap) {
                    h.hide = isNaN(columnHasNoDataMap[i]) || columnHasNoDataMap[i] == this.rows.length;
                    h.excludeFromExport = h.hide;

                    //console.log("hidey", columnHasNoDataMap, i, columnHasNoDataMap[i], columnHasNoDataArray);
                }
            }

            // If we are in mobile view, and we have said to hide, then lets honour it
            if (h.hideOnMobile && this.constantsService.getIsMobile()) {
                h.hide = true;
            }
        });
    }

    setFilterRows(rows: IVeriTableRow<any>[]) {
        //debugger;
        this.filterRows = rows;
        this.searchRows(true);
    }

    onSearch() {
        this.$scope.$applyAsync();
        this.searchRows();

        if (this.veriTableContainer) {
            this.veriTableContainer.onRowsUpdated();
        }
    }

    headerClicked(header: IVeriTableHeader, $index: number) {
        if (!header.sortable) {
            return;
        }

        if (this.sortColumn == $index) {
            if (this.sortDirection == SortDirection.Ascending) {
                this.sortDirection = SortDirection.Descending;
            }
            else {
                this.sortDirection = SortDirection.Ascending;
            }
        }
        else {
            this.sortColumn = $index;
            this.sortDirection = SortDirection.Ascending;
        }

        this.sortRows();
    }

    rowClicked(row: IVeriTableRow<any>, $event: MouseEvent) {
        if (!this.clickable) {
            return;
        }

        this.ignoreNextSelectedItemChange = true;

        if (this.onRowClicked) {
            this.onRowClicked({ row: row, $event: $event });
        }

        if (this.highlightSelectedItems) {
            if (this.selectedItem && this.selectedItem === row.data) {
                this.selectedItem = null;
            }
            else {
                this.selectedItem = row.data;
            }
        }
    }

    rowRightClicked(row: IVeriTableRow<any>, $event: MouseEvent) {
        if (this.onRowRightClicked) {
            this.onRowRightClicked({ row: row, $event: $event });
        }
    }

    rowHighlighted(row: IVeriTableRow<any>, $event: MouseEvent) {
        if (!this.onRowHighlighted || this.highlightedRow == row) {
            return;
        }

        this.highlightedRow = row;

        this.onRowHighlighted({ row: row, $event: $event });
    }

    onDropDownFilterOptionChanged() {
        // Bindings happen as a string, we we need this to be a number for filtering to work
        this.selectedDropDownFilterOption = Number(this.selectedDropDownFilterOption);
        this.searchRows();
    }

    onNavLabelClicked($label: IVeriPillNavLabel) {
        this.selectedNavPillFilterOption = this.navPillFilterOptions.indexOf($label);
        this.searchRows();
    }

    onGroupFilterChanged(filter: IGroupFilter) {
        this.selectedGroup = filter.group;

        this.searchRows();
    }

    onTagFilterChanged(filter: ITagFilter) {
        this.selectedTag = filter.tag;

        this.searchRows();
    }

    toggleGraphOptionsDialog() {
        this.showGraphOptions = !this.showGraphOptions;
    }

    // ---------------------------------------------------

    private prepareHeaders() {
        if (this.showStatusColumn) {
            this.addStatusHeader();
        }

        // Add padding to front and end columns
        if (this.headers && this.headers.length) {
            const paddingToAdd = 5;

            if (this.headers[0].width) {
                this.headers[0].width += paddingToAdd;
            }

            // This is to avoid us setting variable width columns to 5, as opposed to adding 5
            var lastHeaderWidth = this.headers[this.headers.length - 1].width;
            if (!isNaN(lastHeaderWidth) && lastHeaderWidth > 0) {
                this.headers[this.headers.length - 1].width += paddingToAdd;
            }
        }

        this.headers.forEach(header => {
            if (header.width) {
                if (!header.className) {
                    header.className = "";
                }

                header.className += ` width-${header.width}`;
            }

            if (header.alignRight) {
                header.className += " text-right";
            }

            // Validate header has sort type set, if sortable is set
            //console.log(header.sortable, header.sortType);
            //if (header.sortable && header.sortType === undefined) {
            //    console.warn("VeriTable Header Error: Sortable requires SortType", header);
            //}

            if (!header.sortType) {
                header.sortType = SortType.String;
            }

            if (header.templateUrl) {
                header.template = this.$templateCache.get(header.templateUrl);
            }
        });
    }

    private prepareRows() {

        this.displayRows = this.rows;

        this.updateRowType();

        this.rows.forEach(row => {
            if (!row.cells) {
                return;
            }

            if (!this.showStatusColumn && row.rowWarningStatus !== undefined) {
                //console.warn("showStatusColumn is false but rowWarningStatus is defined");
                this.showStatusColumn = true;
                this.addStatusHeader(true);
            }

            if (this.showStatusColumn) {
                this.addStatusCell(row);
            }

            for (var cellIndex = 0; cellIndex < row.cells.length; cellIndex++) {
                const cell = row.cells[cellIndex];

                cell.header = this.headers[cellIndex];

                cell.className = cell.className || "";

                // This doesnt work as we think it does
                // Todo fix
                if (this.sortColumn == cellIndex) {
                    cell.className += " sorted";
                }

                if (cell.header.template) {
                    cell.contents = cell.header.template;
                }


                if (cell.header.sortable) {

                    if (cell.sortData === undefined) {
                        cell.sortData = cell.contents;
                    }

                    if (cell.header.sortType == SortType.String && cell.sortData) {
                        try {
                            cell.sortData = cell.sortData.toLocaleLowerCase();
                        }
                        catch (ex) {
                            console.error(`Failed to create sort cells for column #${cellIndex} ${cell.header.title}, likely due to bad data or through not setting correct Header.SortType`, cell, ex);
                        }
                    }
                }
            };
        });

        this.updateCounts();
    }

    private prepareRowsForSearchAndSort() {

        if (this.hasPreparedRowsForSearchAndSort) {
            return;
        }

        this.hasPreparedRowsForSearchAndSort = true;

        for (var rowIndex = 0; rowIndex < this.rows.length; rowIndex++) {
            const row = this.rows[rowIndex];

            if (!row.cells) {
                continue;
            }

            // Search
            row.searchString = row.searchString || "";

            for (var cellIndex = 0; cellIndex < row.cells.length; cellIndex++) {
                const cell = row.cells[cellIndex];

                if (cell.header?.excludeFromSearch) {
                    continue;
                }

                row.searchString += "||||" + (cell.sortData || cell.contents);

                if (!cell.createSearchCallback) {
                    continue;
                }

                const customSearchString = cell.createSearchCallback(cell.data);

                row.searchString += customSearchString;
            };

            row.searchString = row.searchString.toLocaleLowerCase();
        };
    }

    private updateCounts() {
        this.updateDropDownFilterCount();
        this.updateSimpleNavFilterCount();
    }

    private updateDropDownFilterCount() {
        if (!this.dropDownFilterOptions || this.dropDownFilterOptions.length == 0) {
            return;
        }

        this.dropDownFilterOptions.forEach(option => { option.Count = 0; });

        for (var i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];

            if (!row.dropDownFilterValues) {
                continue;
            }

            for (var j = 0; j < this.dropDownFilterOptions.length; j++) {
                const option = this.dropDownFilterOptions[j];

                if (option.Value <= 0 || row.dropDownFilterValues.indexOf(option.Value) >= 0) {
                    option.Count++;
                }
            }
        }
    }

    private updateSimpleNavFilterCount() {
        if (!this.navPillFilterOptions || this.navPillFilterOptions.length == 0) {
            return;
        }

        this.navPillFilterOptions.forEach(option => { option.count = 0; });

        for (var i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];

            if (!row.navPillFilterValues) {
                continue;
            }

            row.navPillFilterValues.forEach(index => {
                this.navPillFilterOptions[index].count++;
            });
        }
    }

    searchRows(forceDigest = false) {
        this.selectedGroupRowCount = 0;
        this.displayRows = [];

        if (!this.rows) {
            return;
        }

        this.prepareRowsForSearchAndSort();

        const safeSearchString = this.searchString ? this.searchString.trim().toLocaleLowerCase() : null;
        const useRowsFilters = this.filterRows && this.filterRows.length;

        if (!safeSearchString &&
            !useRowsFilters &&
            !this.selectedGroup &&
            !this.dropDownFilterOptions &&
            !this.navPillFilterOptions &&
            !this.selectedTag) {

            this.displayRows = this.rows;
        }
        else {

            for (let ri = 0; ri < this.rows.length; ri++) {
                const row = this.rows[ri];

                let rowMatchedSearchString = false;
                let rowMatchedGroup = true;

                if (!safeSearchString || row.searchString.indexOf(safeSearchString) >= 0) {
                    rowMatchedSearchString = true;
                }

                if (useRowsFilters && !this.filterRows.find(r => r.data.Id ? r.data.Id == row.data.Id : r.data == row.data)) {
                    continue;
                }

                if (this.dropDownFilterOptions && this.selectedDropDownFilterOption > 0 && row.dropDownFilterValues) {
                    rowMatchedGroup = row.dropDownFilterValues.indexOf(this.selectedDropDownFilterOption) >= 0;
                }

                if (this.navPillFilterOptions && this.navPillFilterOptions.length && row.navPillFilterValues) {
                    rowMatchedGroup = row.navPillFilterValues.indexOf(this.selectedNavPillFilterOption) >= 0;
                }

                if (this.selectedGroup) {
                    rowMatchedGroup = row.isInGroup && row.isInGroup(row.data, this.selectedGroup) || !row.isInGroup && this.isRowInGroup(row.data, this.selectedGroup);
                }

                if (rowMatchedGroup) {
                    this.selectedGroupRowCount++;
                }

                const tagIds = row.data && (row.data.TagIds || row.data.tagIds);

                if (!!this.selectedTag && !tagIds?.find(id => id === this.selectedTag.Id)) {
                    continue;
                }

                if (rowMatchedGroup && rowMatchedSearchString) {
                    this.displayRows.push(row);
                }
            }
        }

        this.sortRows();

        // Hammer to crack a angular nut (only needed when linking from Angular component)
        if (forceDigest) {
            this.$scope.$applyAsync();
        }
    }

    clearSearch() {
        if (this.searchBox) {
            this.searchBox.clearSearchTerm();
        }
    }

    private isRowInGroup<T>(data: T, group: IGroupWithIds): boolean {
        if (group == null) {
            return true;
        }

        switch (this.rowType) {
            case ResourceType.Vehicle:
                const vehicle = data as IVehicle;
                return group.Id === 0
                    ? !this.allVehicleGroupIds.includes(vehicle.Id)
                    : group.VehicleIds.indexOf(vehicle.Id) >= 0;

            case ResourceType.Geofence:
                const geofence = data as IGeofence;
                return group.Id === 0
                    ? !this.allGeofenceGroupIds.includes(geofence.Id)
                    : group.GeofenceIds.indexOf(geofence.Id) >= 0;

            case ResourceType.Driver:
                const driver = data as IDriver;
                return group.Id === 0
                    ? !this.allDriverGroupIds.includes(driver.Id)
                    : group.DriverIds.indexOf(driver.Id) >= 0;

            case ResourceType.User:
                const user = data as any;
                return group.Id === 0
                    ? !this.allUserGroupIds.includes(user.Id)
                    : group.userIds.indexOf(user.Id) >= 0;
        }

        console.error("Could not determinte object type");
        return false;
    }

    private updateRowType() {
        if (!this.rows || !this.rows.length) {
            return;
        }

        const data = this.rows[0].data;

        if (!data) {
            return;
        }

        const vehicle: IVehicle = 'ShortReg' in data || 'VehicleType' in data ? data : null;
        const driver: IDriver = 'Name' in data || data.driver ? data : null;
        const user: IUser = 'Username' in data || data.user ? data : null;
        const geofence: IGeofence = 'GeofenceType' in data || data.geofence ? data : null;

        if (vehicle) {
            this.rowType = ResourceType.Vehicle;
        }
        else if (geofence) {
            this.rowType = ResourceType.Geofence;
        }
        else if (driver) {
            this.rowType = ResourceType.Driver;
        }
        else if (user) {
            this.rowType = ResourceType.User;
        }

        if (this.rowType !== undefined || this.rows[0].isInGroup) {
            if (this.showGroupFilter === undefined) {
                this.showGroupFilter = true;
            }

            if (this.showTagFilter === undefined && this.rowType != ResourceType.User) {
                this.showTagFilter = true;
            }
        }

        this.tagResourceType = ResourceType[this.rowType];
    }

    private sortRows() {
        window.performance.mark("veriTable.sortRows");

        const header = this.headers[this.sortColumn];

        if (!header || !this.displayRows || !header.sortable) {
            return;
        }

        this.displayRows.sort((row1, row2) => {

            const cell1 = row1.cells[this.sortColumn];
            const cell2 = row2.cells[this.sortColumn];

            if (cell1.sortData == cell2.sortData) {
                return 0;
            }
            // keep equality (i.e. dont replace with ===) as this will also catch undefined
            else if (cell1.sortData == null) {
                return this.sortDirection === SortDirection.Ascending ? -1 : 1
            }
            else if (cell2.sortData == null) {
                return this.sortDirection === SortDirection.Ascending ? 1 : -1
            }
            else if (header.sortType === SortType.Date) {
                return this.veriDate.sort(cell1.sortData, cell2.sortData, this.sortDirection);
            }
            else if (header.sortType === SortType.Number) {
                const direction = this.sortDirection === SortDirection.Ascending ? -1 : 1;

                return cell1.sortData < cell2.sortData ? 1 * direction : -1 * direction;
            }
            else {
                return this.sortDirection === SortDirection.Ascending
                    ? cell1.sortData.localeCompare(cell2.sortData)
                    : cell2.sortData.localeCompare(cell1.sortData);
            }
        });

        // CJE removed during a test for better null handling, nulls should now appear at the bottom of a list
        //if (this.sortDirection === SortDirection.Descending && columnContainsNulls) {
        //    this.displayRows.reverse();
        //}

        window.performance.measure("veriTable.sortRows");

        this.rowsUpdated();
    }

    private checkForCompactMode() {

        const filterBar = this.$element[0].getElementsByClassName('filters')[0];

        if (filterBar) {
            let filterbarWidth = filterBar.getBoundingClientRect().width;
            let veriTableWidth = this.$element[0].getBoundingClientRect().width;

            if (filterbarWidth > ((veriTableWidth / 100) * 65)) {
                this.showCompactFilters = true;
            }
        }
    }

    private setDefaults() {
        if (this.showRows === undefined) {
            this.showRows = true;
        }

        if (this.sortColumn === undefined) {
            this.sortColumn = 0;
        }

        if (this.sortDirection === undefined) {
            this.sortDirection = SortDirection.Ascending;
        }

        this.selectedItemIdentifier = this.selectedItemIdentifier || "Id";
    }

    private rowsUpdated() {
        if (this.onRowsUpdated) {
            this.onRowsUpdated({ rows: this.displayRows });
        }
    }

    private calculateSelectedItemIndex() {
        if (!this.displayRows) {
            return;
        }

        this.displayRows.forEach((row, i) => {
            if (this.selectedItem && this.selectedItem[this.selectedItemIdentifier] == row.data[this.selectedItemIdentifier]) {
                this.selectedItemIndex = i;
            }
        });
    }

    private addStatusHeader(updateSortColumnPosition = false) {
        if (this.constantsService.getIsMobile()) {
            return;
        }

        this.headers.unshift({
            title: "<i class='material-icons s12 text-muted'>warning</i>",
            excludeFromExport: true,
            width: 35,
            sortable: true,
            sortType: SortType.Number,
            className: "border-spacer width-35" //width-X here is a hack
        });

        if (updateSortColumnPosition) {
            // We do this because the user didnt SPECIFY they wanted this column, we are auto adding it
            this.sortColumn++;
        }
    }

    private addStatusCell(row: IVeriTableRow<any>) {

        if (!this.constantsService.getIsMobile()) {

            var warningCell: IVeriTableRowCell = {
                contents: "",
                sortData: row.rowWarningStatus,
                excludeFromExport: true
            };

            switch (row.rowWarningStatus) {
                case VeriTableRowStatus.Warning:
                    warningCell.className = "warning";
                    warningCell.contents = "<i class='material-icons s12'>warning</i>";
                    break;

                case VeriTableRowStatus.Error:
                    warningCell.className = "error";
                    warningCell.contents = "<i class='material-icons s12'>warning</i>";
                    break;

                case VeriTableRowStatus.OkChecked:
                    warningCell.className = "text-muted";
                    warningCell.contents = "<i class='material-icons s12'>check</i>";
                    break;

                case VeriTableRowStatus.OkDash:
                    warningCell.className = "text-muted";
                    warningCell.contents = "<i class='material-icons s12'>remove</i>";
                    break;
            }

            warningCell.className += " pl-10";

            row.cells.unshift(warningCell);
        }

        row.rowClassName += " " + this.getClassForStatus(row.rowWarningStatus);
    }

    private getClassForStatus(status: VeriTableRowStatus) {
        switch (status) {
            case VeriTableRowStatus.Error:
                return "error";

            case VeriTableRowStatus.Warning:
                return "warning";

            default:
            case VeriTableRowStatus.OK:
                return "ok";
        }
    }

    getLastData(): void {
        if (this.onGetLastAvailableData) {
            this.onGetLastAvailableData();
        }
    }
}

export class VeriTableComponent implements angular.IComponentOptions {
    bindings: any;
    require: { [index: string]: string; };
    controller: any;
    template: string;
    transclude: { [slot: string]: string } | boolean;

    constructor() {

        this.require = {
            parentVeriTable: "?^^veriTable",
            veriTableContainer: "?^veriTableContainer"
        };

        this.bindings = {
            headers: "<",
            rows: "<",

            loading: "<",

            highlightSelectedItems: "<",
            selectedItem: "<",
            selectedItemIdentifier: "@",
            sortColumn: "<",
            sortDirection: "<",

            showStatusColumn: "<",
            showRows: "<",
            showSearch: "<",

            clickable: "<",
            downloadable: "<?",
            withoutGroups: "<",
            maxRows: "<",

            searchString: "<",
            searchPlaceholder: "@",

            showTagFilter: "<",

            showGroupFilter: "<",
            dropDownFilterOptions: "<",
            navPillFilterOptions: "<",

            exportAsLandscape: "<?",
            exportTitle: "@?",
            hideExport: "<?",

            onRowsUpdated: "&",
            onRowClicked: "&",
            onRowRightClicked: "&",
            onRowHighlighted: "&"
        };

        this.transclude = {
            "filters": "?filters",
            "titleStart": "?titleStart",
            "titleEnd": "?titleEnd",
            "emptyState": "?emptyState"
        }

        this.controller = VeriTableComponentController;
        this.template = require("./veriTable.html");
    }
}
